import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { getIn, useFormikContext } from 'formik';
import styles from './HttpBlock.module.scss';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Input from 'component/atom/forms/input/input/Input';
import Button from 'component/atom/forms/button/Button';
import Tooltip from 'component/Custom/tooltip/Tooltip';

const HttpBlock = ({
  title,
  tooltipText,
  fieldName,
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
  typePlaceholder = 'Add Parameter type',
}) => {
  const formik = useFormikContext();

  const items = useMemo(() => {
    const v = getIn(formik.values, fieldName);
    return Array.isArray(v) ? v : [];
  }, [formik.values, fieldName]);

  const [showModal, setShowModal] = useState(false);
  const [draftKey, setDraftKey] = useState('');
  const [draftValue, setDraftValue] = useState('');
  const [parameterType, setParameterType] = useState('');
  const [editIndex, setEditIndex] = useState(null);

  const modalRef = useRef(null);

  const closePanel = useCallback(() => {
    setShowModal(false);
    setDraftKey('');
    setDraftValue('');
    setParameterType('');
    setEditIndex(null);
  }, []);

  useEffect(() => {
    if (!showModal) return;

    const handlePointerDown = (e) => {
      const el = modalRef.current;
      if (!el) return;

      if (!el.contains(e.target)) {
        closePanel();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [showModal, closePanel]);

  const upsertItem = () => {
    const key = (draftKey || '').trim();
    const value = (draftValue || '').trim();
    const ParameterType = (parameterType || '').trim();

    if (!key || !value) return;

    const next = [...items];

    const payload = { key, value, parameterType: ParameterType };

    if (editIndex !== null && editIndex >= 0 && editIndex < next.length) {
      next[editIndex] = payload;
    } else {
      next.push(payload);
    }

    formik.setFieldValue(fieldName, next);
    closePanel();
  };

  const onEdit = (idx) => {
    const current = items[idx];
    setEditIndex(idx);
    setDraftKey(current?.key || '');
    setDraftValue(current?.value || '');
    setParameterType(current?.variableType || '');
    setShowModal(true);
  };

  const onDelete = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    formik.setFieldValue(fieldName, next);
  };

  return (
    <div className={styles['form-block']}>
      <div className={styles['form-block-item']}>
        <div className={styles['http-param-title-container']}>
          <p className={styles['http-param-title']}>{title}</p>
          <Tooltip content={tooltipText} className={styles['tooltip']}>
            <SvgWithBranding
              type='info'
              color='#0744E1'
              className={styles['info']}
            />
          </Tooltip>
        </div>

        <SvgWithBranding
          type='add'
          color='#0957D0'
          className={styles['add-btn']}
          onClick={() => {
            setEditIndex(null);
            setDraftKey('');
            setDraftValue('');
            setParameterType('');
            setShowModal(true);
          }}
        />
      </div>

      {items.map((item, idx) => (
        <div
          key={`${item?.key || 'item'}-${idx}`}
          className={styles['form-block-item']}
        >
          <p className={styles['http-item-title']}>
            {(item?.key || '').trim()}
          </p>
          <div className={styles['actions-container']}>
            <SvgWithBranding
              className={styles['edit-icon']}
              type='edit'
              onClick={() => onEdit(idx)}
            />
            <SvgWithBranding
              className={styles['delete-icon']}
              type='delete2'
              onClick={() => onDelete(idx)}
            />
          </div>
        </div>
      ))}

      <div
        style={{ display: showModal ? 'block' : 'none' }}
        className={styles['add-panel']}
      >
        <div ref={modalRef} className={styles['modal-container']}>
          <Input
            placeholder={keyPlaceholder}
            label='Enter Name'
            value={draftKey}
            onChange={(e) => setDraftKey(e.target.value)}
          />
          <Input
            placeholder={valuePlaceholder}
            label='Enter Value or Variable'
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
          />
          <Input
            placeholder={typePlaceholder}
            label='Parameter type'
            value={parameterType}
            onChange={(e) => setParameterType(e.target.value)}
          />
          <Button
            label={editIndex !== null ? 'Save' : 'Add'}
            variant='light'
            className={styles['add-modal-btn']}
            onClick={upsertItem}
          />
        </div>
      </div>
    </div>
  );
};

export default HttpBlock;
